import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { signUpUser, sendPasswordResetEmail } from '../services/supabase';
import { isValidPassword, PASSWORD_REQUIREMENTS_MESSAGE } from '../shared/passwordValidation';
import { clearPasswordRecoveryPending } from '../utils/passwordRecoveryPending';
import { resolvePostLoginDashboardPath } from '../utils/pendingDashboardDeepLink';
import {
    establishEmailConfirmationSessionFromUrl,
    getAuthCallbackError,
    hasEmailConfirmationCallback,
    hasRecoveryCallbackParams,
    stripAuthParamsFromUrl,
} from '../utils/recoverySession';
import { Input } from '../ui-kit/input';
import { OAuthProviderButtons } from '../components/auth/OAuthProviderButtons';
import { Tetrahedron } from '../components/spinning-logo/spinning-tetrahedron';

const PENDING_OAUTH_CONSENT_PATH = 'pendingOAuthConsentPath';

/**
 * Bottom wordmark: above the 3D logo (z-index). Scales so the full word fits within
 * min(tetrahedron edge, row width) and stays horizontally centered.
 */
function TrussWordmark({ edgePx, className = '' }: { edgePx: number; className?: string }) {
    const rowRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const securityMeasureRef = useRef<HTMLSpanElement>(null);
    const [scale, setScale] = useState(1);
    const [securityScale, setSecurityScale] = useState(1);
    const hasEdge = edgePx >= 8;

    const fontSizePx = hasEdge ? Math.max(20, Math.round(edgePx * 0.5)) : 32;
    const letterSpacingPx = hasEdge ? Math.max(4, Math.round(edgePx * 0.1)) : 5;
    /** Fixed tagline typography (not edge-linked like TRUSS). */
    const securityFontSizePx = 14;
    const securityLetterSpacingEm = 0.17;
    /** Match line box height (`lineHeight` below); `overflow-x:hidden` otherwise clips vertically in some engines. */
    const securityLineHeight = 1.15;

    const recomputeScale = useCallback(() => {
        const row = rowRef.current;
        const rowW = row?.clientWidth ?? 0;
        const cap =
            rowW > 0 ? Math.min(hasEdge ? edgePx : rowW, rowW) : hasEdge ? edgePx : 280;
        if (cap < 1) return;

        const wordNode = measureRef.current;
        if (wordNode) {
            const textW = wordNode.offsetWidth;
            if (textW >= 0.5) setScale(Math.min(1, cap / textW));
        }

        const securityNode = securityMeasureRef.current;
        if (securityNode) {
            const textW = securityNode.offsetWidth;
            if (textW >= 0.5) setSecurityScale(Math.min(1, cap / textW));
        }
    }, [edgePx, hasEdge]);

    useLayoutEffect(() => {
        recomputeScale();
        const row = rowRef.current;
        const ro = row ? new ResizeObserver(() => recomputeScale()) : null;
        if (row && ro) ro.observe(row);
        window.addEventListener('resize', recomputeScale);
        const p = typeof document !== 'undefined' ? document.fonts?.ready : undefined;
        if (p && typeof p.then === 'function') void p.then(recomputeScale);
        return () => {
            window.removeEventListener('resize', recomputeScale);
            ro?.disconnect();
        };
    }, [recomputeScale]);

    const wordmarkMinH = Math.ceil(fontSizePx * scale);
    const securityMinH = Math.ceil(securityFontSizePx * securityScale * securityLineHeight);
    /** Pull tagline toward TRUSS (minH vs. perceived cap height leaves slack). */
    const securityPullUpPx = Math.max(2, Math.round(fontSizePx * scale * 0.08));

    return (
        <div
            ref={rowRef}
            className={`relative z-30 box-border flex w-full min-w-0 shrink-0 flex-col items-stretch overflow-visible ${className}`.trim()}
        >
            {/* Full pane width + flex justify-center: geometric center of TRUSS on horizontal midline; top + minH unchanged. */}
            <div className="relative w-full min-w-0" style={{ minHeight: wordmarkMinH }}>
                <div className="absolute inset-x-0 top-0 flex h-full min-h-0 translate-x-[9px] items-start justify-center overflow-x-hidden">
                    <span
                        className="inline-block whitespace-nowrap will-change-transform"
                        style={{
                            transform: `scale(${scale})`,
                            transformOrigin: 'top center',
                        }}
                    >
                        <span
                            ref={measureRef}
                            className="inline-block font-normal text-foreground"
                            style={{
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontSize: `${fontSizePx}px`,
                                letterSpacing: `${letterSpacingPx}px`,
                                lineHeight: 1.15,
                            }}
                        >
                            TRUSS
                        </span>
                    </span>
                </div>
            </div>
            <div
                className="relative w-full min-w-0"
                style={{ minHeight: securityMinH, marginTop: -securityPullUpPx }}
            >
                <div className="absolute inset-x-0 top-0 flex h-full min-h-0 translate-x-[2px] items-start justify-center overflow-x-clip overflow-y-visible">
                    <span
                        className="inline-block whitespace-nowrap will-change-transform"
                        style={{
                            transform: `scale(${securityScale})`,
                            transformOrigin: 'top center',
                        }}
                    >
                        <span
                            ref={securityMeasureRef}
                            className="inline-block font-normal text-foreground"
                            style={{
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontSize: `${securityFontSizePx}px`,
                                letterSpacing: `${securityLetterSpacingEm}em`,
                                lineHeight: securityLineHeight,
                            }}
                        >
                            SECURITY INFORMATION EXCHANGE
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
}

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);
    const { login, isAuthenticated, authLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isPasswordValid, setIsPasswordValid] = useState(true);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [desktopLogoEdgePx, setDesktopLogoEdgePx] = useState(0);
    const [mobileLogoEdgePx, setMobileLogoEdgePx] = useState(0);
    const validateInputs = () => {
        if (!email || !password) {
            setError('Email and password are required.');
            return false;
        }
        
        if (isRegistering) {
            if (!isValidPassword(password)) {
                setError(PASSWORD_REQUIREMENTS_MESSAGE);
                return false;
            }
            
            if (password !== confirmPassword) {
                setError('Passwords do not match.');
                return false;
            }
        }
        return true;
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateInputs()) return;

        setIsLoading(true);
        setError('');
        try {
            await login(email, password);
            // Redirect is handled by the isAuthenticated effect so password and
            // Google OAuth share the same pending-consent / dashboard resume path.
        } catch (err: any) {
            console.error('Login failed:', err);
            setError(err.message || 'Login failed. Please check your email and password.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateInputs()) return;

        setIsLoading(true);
        setError('');
        setMessage('');
        
        try {
            const response = await signUpUser(email, password);
            if (response.message) {
                setMessage(response.message);
            } else {
                setMessage('Check your email for the confirmation link!');
                setEmail('');
                setPassword('');
            }
        } catch (err: any) {
            console.error('Sign up failed:', err);
            setError(err.message || 'Sign up failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            setError('Please enter your email address.');
            return;
        }

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            await sendPasswordResetEmail(email);
            setMessage('Password reset link has been sent to your email!');
            setIsForgotPassword(false);
        } catch (err: any) {
            console.error('Password reset failed:', err);
            setError(err.message || 'Failed to send reset email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        if (isRegistering) {
            setIsPasswordValid(isValidPassword(newPassword));
        }
    };

    useEffect(() => {
        if (!hasRecoveryCallbackParams() && !getAuthCallbackError()) {
            clearPasswordRecoveryPending();
        }
    }, []);

    useEffect(() => {
        if (!hasEmailConfirmationCallback()) return;

        let cancelled = false;
        void (async () => {
            const callbackError = getAuthCallbackError();
            if (callbackError) {
                if (!cancelled) setError(callbackError);
                stripAuthParamsFromUrl();
                return;
            }

            const result = await establishEmailConfirmationSessionFromUrl();
            if (cancelled) return;

            if (result.ok) {
                setMessage('Your email has been confirmed. You are now signed in.');
                setError('');
            } else if (result.error) {
                setError(result.error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const resetMessage = (location.state as { message?: string } | null)?.message;
        if (resetMessage) {
            setMessage(resetMessage);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    useEffect(() => {
        if (authLoading || !isAuthenticated) return;

        const pendingOAuthConsentPath = localStorage.getItem(PENDING_OAUTH_CONSENT_PATH);
        if (pendingOAuthConsentPath) {
            localStorage.removeItem(PENDING_OAUTH_CONSENT_PATH);
            navigate(pendingOAuthConsentPath);
            return;
        }

        navigate(resolvePostLoginDashboardPath());
    }, [isAuthenticated, authLoading, navigate]);

    return (
        <div className="flex min-h-screen p-8 md:p-12 lg:p-16 bg-background">
            {/* Left Panel */}
            <div className="hidden lg:flex lg:w-1/2 bg-card items-center justify-center p-12 rounded-l-2xl shadow-lg">
                <div className="relative mx-auto flex w-full max-w-lg min-h-0 flex-col overflow-visible rounded-2xl bg-card px-4 pb-4 pt-0 shadow-[inset_0_1px_8px_rgb(0_0_0_/_0.05),inset_0_-2px_12px_rgb(0_0_0_/_0.04)] dark:shadow-[inset_0_1px_8px_rgb(255_255_255_/_0.05),inset_0_-2px_12px_rgb(255_255_255_/_0.04)] sm:px-6 sm:pb-6 sm:pt-0">
                    <div className="relative z-0 mx-auto aspect-square max-h-[350px] max-w-[350px] w-full overflow-visible">
                        <Tetrahedron
                            className="absolute inset-0 z-0 min-h-0 min-w-0 -translate-y-28"
                            width="100%"
                            height="100%"
                            meshBasisMinPx={500}
                            showProof={false}
                            showCaption={false}
                            justifyMesh="start"
                            meshStageAlign="bottom"
                            onLayoutMetrics={({ edgePx }) => setDesktopLogoEdgePx(edgePx)}
                        />
                    </div>
                    <TrussWordmark
                        edgePx={desktopLogoEdgePx}
                        className="pointer-events-none relative z-30 -mt-5 w-full shrink-0 pb-0 pt-0"
                    />
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center bg-card p-8 rounded-2xl lg:rounded-l-none shadow-lg">
                <div className="w-full max-w-md space-y-8">
                    {/* Show logo only on mobile */}
                    <div className="mb-8 flex w-full justify-center px-1 lg:hidden">
                        <div className="relative flex w-full max-w-[350px] min-h-0 flex-col overflow-visible rounded-xl bg-card px-3 pb-3 pt-0 shadow-[inset_0_1px_6px_rgb(0_0_0_/_0.03),inset_0_-2px_12px_rgb(0_0_0_/_0.02)] dark:shadow-[inset_0_1px_6px_rgb(255_255_255_/_0.04),inset_0_-2px_12px_rgb(255_255_255_/_0.03)]">
                            <div className="relative z-0 mx-auto aspect-[5/4] w-full max-w-[350px] overflow-visible">
                                <Tetrahedron
                                    className="absolute inset-0 z-0 min-h-0 min-w-0 -translate-y-20"
                                    width="100%"
                                    height="100%"
                                    meshBasisMinPx={400}
                                    showProof={false}
                                    showCaption={false}
                                    justifyMesh="start"
                                    meshStageAlign="bottom"
                                    onLayoutMetrics={({ edgePx }) => setMobileLogoEdgePx(edgePx)}
                                />
                            </div>
                            <TrussWordmark
                                edgePx={mobileLogoEdgePx}
                                className="pointer-events-none relative z-30 -mt-2 w-full shrink-0 pb-0 pt-0"
                            />
                        </div>
                    </div>

                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-foreground">
                            {isRegistering ? 'Create your account' : 'Welcome back'}
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            {isRegistering 
                                ? 'Sign up to get started' 
                                : 'Please sign in to your account'}
                        </p>
                    </div>

                    {message && (
                        <div className="p-3 text-sm bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-md">
                            {message}
                        </div>
                    )}

                    <form 
                        onSubmit={
                            isForgotPassword 
                                ? handleForgotPassword 
                                : (isRegistering ? handleSignUp : handleLogin)
                        } 
                        className="space-y-4"
                    >
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Email</label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        {!isForgotPassword && (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">Password</label>
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        required
                                        className={isRegistering && !isPasswordValid && password ? 'border-destructive' : ''}
                                    />
                                    {isRegistering && (
                                        <p className={`text-xs ${!isPasswordValid && password ? 'text-destructive' : 'text-muted-foreground'} mt-1`}>
                                            {PASSWORD_REQUIREMENTS_MESSAGE}
                                        </p>
                                    )}
                                </div>
                                {isRegistering && (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-foreground">Confirm Password</label>
                                        <Input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {!isRegistering && !isForgotPassword && (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsForgotPassword(true);
                                    setError('');
                                    setMessage('');
                                }}
                                className="text-sm text-muted-foreground hover:text-foreground"
                            >
                                Forgot your password?
                            </button>
                        )}
                        {error && <p className="text-destructive text-sm">{error}</p>}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-2 px-4 rounded-md text-primary-foreground transition-colors ${
                                isLoading ? 'bg-muted cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
                            }`}
                        >
                            {isLoading 
                                ? (isForgotPassword ? 'Sending...' : (isRegistering ? 'Signing up...' : 'Signing in...'))
                                : (isForgotPassword ? 'Send Reset Link' : (isRegistering ? 'Sign Up' : 'Sign in'))}
                        </button>
                        
                        <div className="text-center pt-2">
                            <span className="text-sm text-muted-foreground">
                                {isRegistering ? 'Already have an account? ' : 'Need an account? '}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsRegistering(!isRegistering);
                                    setIsForgotPassword(false);
                                    setError('');
                                    setMessage('');
                                }}
                                className="text-sm text-primary hover:text-primary/80"
                            >
                                {isRegistering ? 'Sign in' : 'Sign up'}
                            </button>
                        </div>
                    </form>

                    {!isForgotPassword && (
                        <OAuthProviderButtons
                            disabled={isLoading}
                            onError={setError}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;